import { CommandDefinition, defineCommand } from '../../../concepts/commands/CommandDefinition';
import { Component, ComponentType, ObjectManager, component, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { resolveSubjects } from '../../canvas/commands/CommandUtil';
import { AutoBringToFront } from '../../canvas/entities/AutoBringToFront';
import { CanSelectWhenLocked } from '../../canvas/entities/CanSelectWhenLocked';
import { DropInfo, DropTarget } from '../../canvas/entities/DropTarget';
import { Phys } from '../../canvas/entities/Phys';
import { GuideLine, Physical, PotentialInteraction, RelativeTo, TransformHints } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Resizable } from '../../canvas/entities/Resizable';
import { Snappable } from '../../canvas/entities/Snappable';
import { UIField } from '../../canvas/entities/UIField';
import { AutoLayout, AutoLayoutMode, AutoLayoutSubject } from '../autoLayout/AutoLayout';
import { FlexSorter } from '../autoLayout/FlexLayout';
import { SmartContainer } from '../controls/SmartContainer';
import { herdToElement } from '../herding/HerdingCommands';
import { FrameAddon } from './FrameAddon';
import { FrameLabelFontSizeAttr } from './FrameAssociations';
import { FrameLayout } from './FrameLayout';
import { framePropertyLayout } from './FramePropertyLayout';
import { FrameTitleField } from './FrameTitleField';
import { ActivityFrameAddon } from './addons/ActivityFrameAddon';
import { FrameAddonWithDimensions } from './addons/FrameAddonWithDimensions';

const mutation = defineCommand(
    'frameMutation',
    null,
    (cc, [id, hint]: [string, string?]) => {
        const session = cc.resolve(EditorSession);
        if (hint === 'default') {
            session.update(AdapterInfo, CreateAdapterInfo(id, 'field'));
        }
    },
    [],
);

const invocation = defineCommand(
    'frameInvocation',
    null,
    (cc, [id, hint]: [string, string?]) => {
        const objects = cc.resolve(ObjectManager);
        const frame = objects.getMaybe(id, Frame);
        const activity = frame.addon(ActivityFrameAddon);
        if (activity) {
            if (hint === 'hideShow') {
                objects.update(() => activity.hidden = !activity.hidden);
            }
            if (hint === 'herd') {
                cc.dispatch(herdToElement, [frame.id]);
            }
        }
    },
    [],
);

const frameDef = {
    name: 'Frame',
    implements: [Physical, AutoLayout, DropTarget, SmartContainer, Resizable, Snappable, CanSelectWhenLocked],
    mutation,
    invocation,
    propertyLayout: framePropertyLayout,
};

const frameDrop = defineCommand(
    'frameDrop',
    null,
    (r, [id, targetIds]: [string, string[]]) => {

        const objects = r.call(ObjectManager);

        const frame = objects.get(id, Frame);
        const bounds = frame.bounds(RelativeTo.Model);

        const subjects = resolveSubjects(r, targetIds);

        if (!subjects.length) return;

        objects.update(() => {
            for (const s of subjects) {
                // Only actually drop if subject "in" frame
                const abs = s.createFigure();
                if (bounds.contains(s.outline(RelativeTo.Model))) {
                    frame.add(s.removeSelf());
                    s.setFromFigure(abs);
                }
            }
        });
    }
);

@component(frameDef)
export class Frame extends RectangularPhysical implements AutoLayout, DropTarget, SmartContainer, Snappable {

    @property({ type: 'string', oldName: 'label|value' })
    public title: string;

    @property({ type: 'boolean', default: true })
    public titleVisible: boolean;

    @property({ type: 'string', style: true })
    public background: string;

    public get boundary() {
        return this.delegateToAddon('hidden', false);
    }

    public get layoutMode() {
        return this.delegateToAddon('layoutMode', AutoLayoutMode.none);
    }

    @memoUsing('width', 'height', 'title', 'titleVisible', FrameLabelFontSizeAttr)
    public get layout() {
        return FrameLayout(this);
    }

    @memoUsing('layout', 'title', 'titleVisible')
    public get uiFields(): readonly UIField[] {
        if (this.titleVisible) {
            return [FrameTitleField(this)];
        }
        return [];
    }

    public get minWidth() {
        return 20;
    }

    public get minHeight() {
        return 20;
    }

    public get shouldSnap() {
        return true;
    }

    public addon<T extends FrameAddon = FrameAddon>(type?: ComponentType<T>): T {
        return this.children.find(x => x.is(type ?? FrameAddon)) as T;
    }

    public queryTransformHints() {
        return TransformHints({
            scaleX: true,
            scaleY: true,
            translate: true,
            visible: true,
        });
    }

    public area(to?: Physical | RelativeTo): Rect {
        const { layout } = this;
        const mt = this.transform(to);
        const base = Rect.fromMany([
            layout.base,
            layout.title,
        ]);
        return Rect.fromPoints(mt.apply(base));
    }

    public layoutCells(to?: RelativeTo): Rect[] {
        const addon = this.addon();
        if (!!addon && 'layoutCells' in addon) {
            const mt = this.transform(to);
            return addon.layoutCells(mt);
        }
        return [];
    }

    public getPhysicalsNaturallyOrdered(): Physical[] {
        const [axis] = this.layoutMode;
        return FlexSorter.sort(
            (!axis || axis === 'none' ? axis : 'row') as any,
            this.polarize(AutoLayoutSubject)
        );
    }

    public queryDrop(mpt: Point, subjects: Iterable<Component>): DropInfo {

        const phys = Phys.from(subjects);
        if (phys.includes(this)) return null;

        const frame = Polyline.from(
            this.transform(RelativeTo.Model).apply(this.layout.base)
        );

        if (!frame.contains(mpt)) return null;

        // Some must not be already in frame
        if (phys.whereNotDescendantOf(this).size() === 0) return null;

        // Show guide if not all auto bring to front
        const showGuide = phys.filter(x => !x.is(AutoBringToFront)).size() > 0;

        return {
            target: this,
            guides: showGuide ? [{ points: Array.from(frame.points), }] : [],
            command: frameDrop,
        };
    }

    public queryPipe(_subject: Physical): CommandDefinition<[string, string]> {
        return null;
    }

    public queryGuides(): GuideLine[] {
        return this.addon()?.queryGuides?.() ?? super.queryGuides();
    }

    public test(mpt: Point): boolean {
        const addon = this.addon();
        if (!!addon && 'test' in addon && addon.test(mpt)) {
            return true;
        }

        const { base, title } = this.layout;
        const lpt = this.toRelative(mpt);

        if (this.boundary) {
            return base.contains(lpt) || title.contains(lpt);
        }
        else {
            return title.contains(lpt);
        }
    }

    public testInteraction(pi: PotentialInteraction): [boolean, string] {
        const addon = this.addon();

        if ('testInteraction' in addon) {
            const [hit, hint] = addon.testInteraction(pi);
            if (hit) {
                return [true, hint];
            }
        }

        const { title } = this.layout;
        const lpt = this.toRelative(pi.mpt);

        if (pi.type !== 'invoke' && (title.contains(lpt))) {
            return [true, 'default'];
        }

        return [false, 'none'];
    }

    public toHumanString(): string {
        return `Frame ("${this.title}")`;
    }

    protected setTransformationResult(x: number, y: number, r: number, w: number, h: number): void {
        const addon = this.addon()?.as(FrameAddonWithDimensions);
        if (!!addon) {
            const cs = addon.getCorrectedSize(w, h);
            w = cs.w;
            h = cs.h;
        }
        super.setTransformationResult(x, y, r, w, h);
    }

    private delegateToAddon<T = any>(name: string, fallack?: T): T {
        const addon = this.addon();
        if (addon && addon.has(name)) {
            return addon.get(name);
        }
        return fallack ?? null;
    }
}
